import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PrimaryButton,
  SecondaryButton,
  StateButton,
} from '../../components/atoms/Button/Button';
import Rule from '../../components/atoms/Rule';
import Card from '../../components/molecules/Card/Card';
import ControlBar from '../../components/molecules/ControlBar/ControlBar';
import Loader from '../../components/molecules/Loader';
import GrandTotalEstimateCard from '../../components/organisms/GrandTotalEstimateCard/GrandTotalEstimateCard';
import Heading from '../../components/typography/Heading';
import Section from '../../components/wrappers/Section';
import { createQuoteFromBid } from '../../types/Quote';
import Trip from '../../types/Trip';
import getTripKeyDetails from '../../utilities/getTripKeyDetails';
import { ROUTES } from '../routerConfig';
import UnavailableTrip from '../Trip/PilotView/UnavailableTrip';
import FinalExpensesAlterations from '../Trip/subcomponents/FinalExpensesCard/FinalExpensesAlterations';
import FinalExpensesTable from '../Trip/subcomponents/FinalExpensesCard/FinalExpensesTable';
import { FinalExpenses } from '../../types/FinalExpenses';
import Form from '../../components/organisms/Form/Form';
import validFinalExpenses from '../../types/ValidFinalExpenses';
import getTripAsConsumer from '../../API/Trips/getTripAsConsumer';
import ExpenseAndAlterationSummaryCard from '../../components/organisms/ExpenseAndAlterationSummaryCard/ExpenseAndAlterationSummaryCard';
import { useModalContext } from '../../hooks/ModalProvider/ModalProvider';
import QuoteModal from '../Trip/QuoteModal';
import TripDetailsModal from '../Trip/PilotView/TripDetailsModal';
import approveExpenses from '../../API/Trips/approveExpenses';
import makePayment from '../../API/Payments/makePayment';
import rejectExpenses from '../../API/Trips/rejectExpenses';
import { useToast } from '../../hooks/ToastProvider';

function ReviewExpensesPage() {
  const { id } = useParams();
  const { openModal, closeModal } = useModalContext();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [loadingTrip, setLoadingTrip] = useState(true);
  const [failedToLoadTrip, setFailedToLoadTrip] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load trip
  useEffect(() => {
    const getAndSetTrip = async () => {
      try {
        const loadedTrip = await getTripAsConsumer(id as string);

        if (loadedTrip.status !== 'Expenses Submitted')
          navigate(`/trips/${loadedTrip.id}`);

        setTrip(loadedTrip);
        setLoadingTrip(false);
      } catch (error) {
        setTrip(null);
        setFailedToLoadTrip(true);
        setLoadingTrip(false);
      }
    };
    getAndSetTrip();
  }, [id, navigate]);

  if (failedToLoadTrip) return <UnavailableTrip />;
  if (loadingTrip)
    return (
      <Card responsive>
        <Loader level={3} caption="Loading Trip..." />
      </Card>
    );
  if (!trip) return <Section>No trip found</Section>;
  if (!trip.conversations.length || !trip.conversations[0].bid)
    navigate(ROUTES.TRIPS);

  const { hiredBid, hiredBidConversation } = getTripKeyDetails(trip);

  if (!hiredBid || !hiredBidConversation) {
    throw new Error(
      `No hired bid/hired bid conversation on trip id: ${trip.id}`
    );
  }

  const quote = createQuoteFromBid(
    hiredBid,
    hiredBidConversation.user,
    trip,
    false
  );

  const expenses: FinalExpenses = {
    itemizedExpenses: hiredBid.expenses,
    alterations: hiredBid.alteration,
  };

  const openQuoteModal = () => {
    openModal(
      <QuoteModal
        conversation={hiredBidConversation}
        isQuoted
        isPilot={false}
      />
    );
  };

  const openDetailsModal = (trip: Trip) => {
    openModal(<TripDetailsModal trip={trip} />);
  };

  const handleApprove = async () => {
    try {
      setSubmitting(true);
      await approveExpenses(trip.id);
      await makePayment(trip.id);
      setSubmitting(false);
      navigate(`${ROUTES.TRIPS}/${trip.id}`);
    } catch (err) {
      addToast(<span>Unable to finalize trip.</span>, 'error');
      navigate(`${ROUTES.TRIPS}/${trip.id}`);
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      await rejectExpenses(trip.id);
      navigate(`${ROUTES.TRIPS}/${trip.id}`);
    } catch (err) {
      addToast(<span>{(err as Error).message}</span>, 'error');
      navigate(`${ROUTES.TRIPS}/${trip.id}`);
    }
  };

  return (
    <div className="relative">
      {/* Mobile secondary top menu */}
      <ControlBar>
        <PrimaryButton
          className="bg-white"
          outline
          size="small"
          onClick={() => navigate(`${ROUTES.TRIPS}/${id}`)}
        >
          <i className="fa-solid fa-arrow-left" /> Back to Trip
        </PrimaryButton>
        <Rule className="md:hidden" />
        <SecondaryButton size="small" onClick={openQuoteModal}>
          Review Quote
        </SecondaryButton>
        <SecondaryButton size="small" onClick={() => openDetailsModal(trip)}>
          View Trip
        </SecondaryButton>
        <StateButton
          className="md:ml-auto"
          size="small"
          onClick={handleReject}
          status="error"
        >
          Reject
        </StateButton>
        <StateButton
          status="success"
          size="small"
          onClick={handleApprove}
          disabled={submitting}
        >
          <span>Approve</span>
        </StateButton>
      </ControlBar>
      <div className="flex flex-col gap-4">
        <Heading level={2}>
          Reviewing Expenses for &ldquo;{trip.name}&rdquo;
        </Heading>
        <Form
          validationSchema={validFinalExpenses}
          isCancellable
          onSubmit={handleApprove}
          onCancel={handleReject}
          initialValues={expenses}
          cancelLabel="Reject"
          submitLabel="Accept Expenses"
          readOnly
          disabled={submitting}
        >
          <div className="w-full relative grid grid-cols-1 gap-4 lg:gap-8 lg:grid-cols-3 overflow-y-scroll">
            <Card responsive title="Expenses" className="col-span-2">
              <div className="flex flex-col gap-4">
                <FinalExpensesAlterations readonly trip={trip} />
                <Rule />
                <FinalExpensesTable readonly />
              </div>
            </Card>
            <ExpenseAndAlterationSummaryCard trip={trip} />
          </div>
        </Form>
      </div>
    </div>
  );
}

export default ReviewExpensesPage;
